/** Host OS — AI Agents overview. Home for every AI capability. Starts with
 *  two agents: the Voice Agent (inbound calls) and the Internal ChatGPT
 *  (your Knowledge Base assistant). */
import { useNavigate } from "react-router-dom";
import { PhoneCall, Bot, ArrowRight, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader } from "@/components/host/hostUi";

const AGENTS = [
  {
    key: "assistant",
    label: "Internal ChatGPT",
    tagline: "Answers questions from your Knowledge Base",
    desc: "A private ChatGPT trained on your website, documents and FAQs. Your team asks it anything about your business and gets reliable, sourced answers.",
    icon: Bot,
    path: "/host/ai/assistant",
    status: "ready",
    accent: "from-emerald-500/20 to-teal-500/10 text-emerald-700 dark:text-emerald-300",
    ring: "hover:border-emerald-500/50",
  },
  {
    key: "voice",
    label: "Voice Agent",
    tagline: "Picks up your inbound phone calls",
    desc: "A natural-sounding voice agent that answers calls 24/7 — books classes, answers questions and takes messages so no lead ever goes to voicemail.",
    icon: PhoneCall,
    path: "/host/ai/voice",
    status: "soon",
    accent: "from-violet-500/20 to-fuchsia-500/10 text-violet-700 dark:text-violet-300",
    ring: "hover:border-violet-500/50",
  },
];

const StatusBadge = ({ status }) =>
  status === "ready" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" data-testid="agent-status-ready">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Ready
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground" data-testid="agent-status-soon">
      Coming soon
    </span>
  );

const HostAIAgentsPage = () => {
  const navigate = useNavigate();

  return (
    <HostLayout>
      <div data-testid="host-ai-agents-page">
        <HostPageHeader
          title="AI Agents"
          subtitle="Your AI workforce. Each agent handles a job for you — answering questions, taking calls and more over time."
        />

        <div className="grid sm:grid-cols-2 gap-4">
          {AGENTS.map((a) => {
            const Icon = a.icon;
            return (
              <Card
                key={a.key}
                onClick={() => navigate(a.path)}
                className={`group p-5 cursor-pointer border transition-all ${a.ring} hover:shadow-md`}
                data-testid={`agent-card-${a.key}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${a.accent} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <h3 className="font-display text-xl leading-tight">{a.label}</h3>
                <p className="text-sm font-medium text-muted-foreground mt-0.5">{a.tagline}</p>
                <p className="text-sm text-muted-foreground mt-3">{a.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-ink group-hover:gap-2 transition-all">
                  {a.status === "ready" ? "Open agent" : "Preview"}
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="w-3.5 h-3.5" />
          More agents are on the way — booking, follow-ups and reviews.
        </div>
      </div>
    </HostLayout>
  );
};

export default HostAIAgentsPage;
